/**
 * The ceiling line is the top of an interior elevation's drawing.
 *
 * An elevation object is an absolutely-positioned box with `overflow:
 * visible`, and several schematics deliberately draw ABOVE their own box —
 * a vent hood's duct cover rises to the ceiling, a wood stove's flue runs
 * off the top of the stove. Nothing says where the ceiling is, so those
 * risers keep going past it, and an object taller than the wall it stands
 * against (a stair, a tall pantry on a low wall) draws straight through the
 * ceiling line the wall rectangle just drew.
 *
 * This clips each object at the ceiling of the wall SEGMENT it is drawn
 * against — only at the top: the sides and the bottom stay unbounded,
 * because objects legitimately overhang a wall's ends (the preview tiles
 * pad for exactly that) and a floor-standing object's box may dip below
 * the floor line.
 *
 * `polygon()` rather than `inset()` with negative lengths: a polygon's
 * points are plainly allowed outside the border box, so the unbounded
 * sides need no reliance on how negative insets are resolved.
 */

/** Half-extent of the "unbounded" sides, in px. Larger than any drawing. */
export const CEILING_CLIP_BLEED_PX = 10000;

/**
 * A CSS `clip-path` that hides everything above the ceiling.
 *
 * @param {number} ceilingY2D - Ceiling line, in container px from the top
 *   of the elevation container (the top of the object's wall segment).
 * @param {number} boxTopPx - Top of the object's own box, same frame.
 * @returns {string|undefined} `clip-path` value, or undefined when either
 *   number is missing — an unknown ceiling clips nothing.
 */
export function ceilingClipPath(ceilingY2D, boxTopPx) {
  if (!Number.isFinite(ceilingY2D) || !Number.isFinite(boxTopPx)) {
    return undefined;
  }
  // The ceiling expressed in the object box's own coordinates: negative
  // when the ceiling is above the box (the usual case — the clip then only
  // bites on what the schematic draws above itself), positive when the box
  // itself pokes through the ceiling.
  const y = ceilingY2D - boxTopPx;
  const b = CEILING_CLIP_BLEED_PX;
  return `polygon(${-b}px ${y}px, ${b}px ${y}px, ${b}px ${b}px, ${-b}px ${b}px)`;
}
